// src/components/team/QuickStatusHistory.jsx
import React, { useState, useEffect } from 'react';
import { Clock, User, FileText, TrendingUp, Filter, Download } from 'lucide-react';
import { statusService } from '../../services/statusService';

export const QuickStatusHistory = ({
  bookId,
  galleryId,
  mediaId,
  statusType = null,
  limit = 10,
  compact = false
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(statusType || 'all');

  useEffect(() => {
    loadHistory();
  }, [bookId, galleryId, mediaId, filterType]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const filters = {
        statusType: filterType !== 'all' ? filterType : null,
        limit
      };
      const data = await statusService.getMemberStatusHistory(bookId, galleryId, mediaId, filters);
      setHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const exportHistory = () => {
    const csv = [
      ['Date', 'Status Type', 'Status', 'Notes', 'Recorded By'].join(','),
      ...history.map(h => [
        formatDate(h.timestamp),
        h.statusType,
        h.statusLabel,
        `"${h.notes || ''}"`,
        h.recordedBy
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `member-status-history-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        {history.slice(0, 3).map((update) => (
          <div 
            key={update.id}
            className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded-lg"
          >
            <span className="text-lg">{update.statusIcon}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{update.statusLabel}</p>
              <p className="text-xs text-gray-500">{formatDate(update.timestamp)}</p>
            </div>
          </div>
        ))}
        {history.length > 3 && (
          <p className="text-xs text-center text-gray-500">
            +{history.length - 3} more updates
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-blue-600" />
          Status History
        </h4>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="wellbeing">Wellbeing</option>
            <option value="attendance">Attendance</option>
            <option value="performance">Performance</option>
            <option value="mood">Mood</option>
            <option value="availability">Availability</option>
          </select>
          <button
            onClick={exportHistory}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Export history"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Timeline */}
      {history.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No status updates yet</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {history.map((update, index) => (
            <div 
              key={update.id}
              className="relative pl-6 pb-3 border-l-2 border-gray-200 last:border-transparent"
            >
              {/* Timeline dot */}
              <div 
                className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-white"
                style={{ backgroundColor: update.statusColor }}
              />

              {/* Content */}
              <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{update.statusIcon}</span>
                    <div>
                      <p className="font-medium text-gray-900">{update.statusLabel}</p>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{update.statusType}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{formatDate(update.timestamp)}</span>
                </div>

                {update.notes && (
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700 flex items-start gap-2">
                    <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p>{update.notes}</p>
                  </div>
                )}

                <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                  <User className="h-3 w-3" />
                  <span>{update.recordedBy}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};